import { useState, useEffect } from 'react';
import {
  ClockIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const FREQUENCIES = [
  { value: 'manual', label: 'Manual Only', description: 'Sync only when triggered manually' },
  { value: 'hourly', label: 'Hourly', description: 'Sync every hour' },
  { value: 'daily', label: 'Daily', description: 'Sync once per day' },
  { value: 'weekly', label: 'Weekly', description: 'Sync once per week' },
  { value: 'monthly', label: 'Monthly', description: 'Sync once per month' },
  { value: 'yearly', label: 'Yearly', description: 'Sync once per year' }
];

export default function SyncScheduleEditor({ schedule, companyTimezone = 'UTC', onSave, onCancel, isSaving }) {
  const [localSchedule, setLocalSchedule] = useState({
    enabled: schedule?.enabled || false,
    frequency: schedule?.frequency || 'manual',
    importMode: schedule?.importMode || 'full',
    minute: schedule?.minute || 0,
    hour: schedule?.hour || 0,
    dayOfWeek: schedule?.dayOfWeek || 0,
    dayOfMonth: schedule?.dayOfMonth || 1,
    month: schedule?.month || 1
    // Note: timezone is set automatically from company settings on the backend
  });

  // Format timezone for display (e.g., "America/New_York" -> "America/New York")
  const formatTimezone = (tz) => {
    if (!tz) return 'UTC';
    return tz.replace(/_/g, ' ');
  };

  useEffect(() => {
    // Auto-enable when frequency changes from manual
    if (localSchedule.frequency !== 'manual' && !localSchedule.enabled) {
      setLocalSchedule(prev => ({ ...prev, enabled: true }));
    }
    // Auto-disable when frequency is manual
    if (localSchedule.frequency === 'manual' && localSchedule.enabled) {
      setLocalSchedule(prev => ({ ...prev, enabled: false }));
    }
  }, [localSchedule.frequency, localSchedule.enabled]);

  const updateField = (field, value) => {
    setLocalSchedule(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(localSchedule);
  };

  // Generate hours options (00-23)
  const hours = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: i.toString().padStart(2, '0') + ':00'
  }));

  // Generate minutes options (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45].map(m => ({
    value: m,
    label: m.toString().padStart(2, '0')
  }));

  // Generate days of month (1-31)
  const daysOfMonth = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: (i + 1).toString()
  }));

  const getScheduleDescription = () => {
    if (localSchedule.frequency === 'manual') {
      return 'Sync will only run when triggered manually.';
    }

    const timeStr = `${localSchedule.hour.toString().padStart(2, '0')}:${localSchedule.minute.toString().padStart(2, '0')}`;
    const tz = formatTimezone(companyTimezone);
    
    switch (localSchedule.frequency) {
      case 'hourly':
        return `Sync will run every hour at minute ${localSchedule.minute.toString().padStart(2, '0')} (${tz} timezone).`;
      case 'daily':
        return `Sync will run every day at ${timeStr} (${tz} timezone).`;
      case 'weekly':
        const dayName = DAYS_OF_WEEK.find(d => d.value === localSchedule.dayOfWeek)?.label;
        return `Sync will run every ${dayName} at ${timeStr} (${tz} timezone).`;
      case 'monthly':
        return `Sync will run on day ${localSchedule.dayOfMonth} of every month at ${timeStr} (${tz} timezone).`;
      case 'yearly':
        const monthName = MONTHS.find(m => m.value === localSchedule.month)?.label;
        return `Sync will run on ${monthName} ${localSchedule.dayOfMonth} at ${timeStr} every year (${tz} timezone).`;
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Sync Mode */}
      <div>
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
          Sync Mode
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => updateField('importMode', 'full')}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${
              localSchedule.importMode === 'full'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-surface-200 dark:border-surface-700 hover:border-surface-300'
            }`}
          >
            <div className="font-medium text-surface-900 dark:text-white">Full Refresh</div>
            <div className="text-sm text-surface-500 mt-1">
              Replace all existing data with new data
            </div>
          </button>
          <button
            type="button"
            onClick={() => updateField('importMode', 'incremental')}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${
              localSchedule.importMode === 'incremental'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-surface-200 dark:border-surface-700 hover:border-surface-300'
            }`}
          >
            <div className="font-medium text-surface-900 dark:text-white">Incremental</div>
            <div className="text-sm text-surface-500 mt-1">
              Only add new records since last sync
            </div>
          </button>
        </div>
      </div>

      {/* Frequency */}
      <div>
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
          Sync Frequency
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {FREQUENCIES.map(freq => (
            <button
              key={freq.value}
              type="button"
              onClick={() => updateField('frequency', freq.value)}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                localSchedule.frequency === freq.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-surface-200 dark:border-surface-700 hover:border-surface-300'
              }`}
            >
              <div className="font-medium text-surface-900 dark:text-white text-sm">{freq.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Time Settings */}
      {localSchedule.frequency !== 'manual' && (
        <div className="space-y-4 p-4 bg-surface-50 dark:bg-surface-800/50 rounded-lg">
          <h4 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
            <ClockIcon className="w-5 h-5" />
            Schedule Settings
          </h4>

          {/* Yearly: Month selection */}
          {localSchedule.frequency === 'yearly' && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Month</label>
              <select
                value={localSchedule.month}
                onChange={(e) => updateField('month', parseInt(e.target.value))}
                className="input"
              >
                {MONTHS.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Monthly/Yearly: Day of month selection */}
          {(localSchedule.frequency === 'monthly' || localSchedule.frequency === 'yearly') && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Day of Month</label>
              <select
                value={localSchedule.dayOfMonth}
                onChange={(e) => updateField('dayOfMonth', parseInt(e.target.value))}
                className="input"
              >
                {daysOfMonth.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Weekly: Day of week selection */}
          {localSchedule.frequency === 'weekly' && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Day of Week</label>
              <select
                value={localSchedule.dayOfWeek}
                onChange={(e) => updateField('dayOfWeek', parseInt(e.target.value))}
                className="input"
              >
                {DAYS_OF_WEEK.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Time selection (for daily, weekly, monthly, yearly) */}
          {['daily', 'weekly', 'monthly', 'yearly'].includes(localSchedule.frequency) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Hour</label>
                <select
                  value={localSchedule.hour}
                  onChange={(e) => updateField('hour', parseInt(e.target.value))}
                  className="input"
                >
                  {hours.map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Minute</label>
                <select
                  value={localSchedule.minute}
                  onChange={(e) => updateField('minute', parseInt(e.target.value))}
                  className="input"
                >
                  {minutes.map(m => (
                    <option key={m.value} value={m.value}>:{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Hourly: Minute selection only */}
          {localSchedule.frequency === 'hourly' && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">
                At minute
              </label>
              <select
                value={localSchedule.minute}
                onChange={(e) => updateField('minute', parseInt(e.target.value))}
                className="input w-32"
              >
                {minutes.map(m => (
                  <option key={m.value} value={m.value}>:{m.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Schedule Description */}
          <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
            <div className="flex items-start gap-2">
              <CalendarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-primary-700 dark:text-primary-300">
                {getScheduleDescription()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-surface-200 dark:border-surface-700">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isSaving}
        >
          <XMarkIcon className="w-5 h-5 mr-2" />
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="btn-primary"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="w-5 h-5 mr-2" />
              Save Schedule
            </>
          )}
        </button>
      </div>
    </div>
  );
}

